// src\theme\theme.ts
import { createTheme, ThemeOptions } from "@mui/material/styles";

// Define theme options
const themeOptions: ThemeOptions = {
    palette: {
        primary: {
            main: "#002F73", // Blue
        },
        secondary: {
            main: "#ff4081", // Pink
        },
        background: {
            default: "#fff",
            paper: "#fff", // Light gray        
        },
        text: {
            primary: "#333333",
            secondary: "#3A3A3A",
        },
        success: {
            main: '#54a442',
            light: '#81C784',
            dark: '#388E3C',
            contrastText: '#fff'
        },
        error: {
            main: '#e14343',
            light: '#E57373',
            dark: '#C62828',
            contrastText: '#fff',
        },
        warning: {
            light: '#ffcc80',
            main: '#ffa726',
            dark: '#fb8c00',
            contrastText: '#000',
        },
        info: { main: "#0288d1" },

    },
    typography: {
        fontFamily: "Poppins, sans-serif",
        h1: { fontSize: "2.5rem", fontWeight: 700 },
        h2: {
            fontSize: "24px", fontWeight: 500, color: '#000',
            '&.page-tl': {
                fontSize: 24,
                lineHeight: '28px',
                fontWeight: 600,
                '& img': {
                    marginRight: 18,
                }
            }
        },
        h3: { fontSize: "22px", lineHeight: "30px", fontWeight: 500 },
        h4: { fontSize: "20px", lineHeight: "24px", fontWeight: 500, },
        h5: { fontSize: "16px", lineHeight: "20px", fontWeight: 500, },
        h6: { fontSize: "14px", lineHeight: "16px", fontWeight: 600, },
        body1: { fontSize: "1rem", lineHeight: 1.6 },
        button: { textTransform: "none", fontWeight: 600 },
    },
    shape: {
        borderRadius: 12, // Softer rounded corners
    },
    components: {
         MuiCssBaseline: {
            styleOverrides: {
                'div:where(.swal2-container)': {
                    zIndex: 2000,
                 },
                 ".MuiAutocomplete-root.autocomplete-style .MuiOutlinedInput-root": {
                    padding: '0 !important'
                }
            },
        },
        MuiButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: 4,
                    padding: "8px 16px",
                    minHeight: '42px',
                    textTransform: "none",
                    fontSize: 16,
                    lineHeight: '22px',
                    fontWeight: 400,
                    '&.rounded': {
                        borderRadius: '100px !important',
                    },
                    
                    '&.MuiButton-containedPrimary': {
                        '&:not(:disabled)': {
                            backgroundColor: '#002F73',
                            '& .MuiButton-icon': {
                                filter: 'brightness(0) invert(1)',
                            },
                            '& .MuiButton-endIcon': {
                                marginLeft: theme.spacing(0.5),
                            },
                            '& .MuiButton-startIcon': {
                                marginRight: theme.spacing(0.5),
                            },
                            '& img': {
                                filter: 'brightness(0) invert(1)',
                                marginLeft: theme.spacing(0.5),
                                marginRight: theme.spacing(0.5),
                            },
                            '&:hover': {
                                backgroundColor: '#3C6296',
                            },
                        },
                        
                        '&.green':{
                            borderColor: theme.palette.success.dark,
                            backgroundColor: theme.palette.success.dark,
                            '&:hover': {
                                borderColor: theme.palette.success.main,
                                backgroundColor: theme.palette.success.main,
                            },
                        },

                        '&.error':{
                            borderColor: theme.palette.error.dark,
                            backgroundColor: theme.palette.error.dark,
                            '&:hover': {
                                borderColor: theme.palette.error.main,
                                backgroundColor: theme.palette.error.main,
                            },
                        },
                    },
                    '&.MuiButton-outlined': {
                        borderWidth: 1,
                        '&.MuiButton-outlinedPrimary': {
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                                color: '#fff',
                                '& .MuiButton-icon': {
                                    filter: 'brightness(0) invert(1)'
                                },
                                '& img': {
                                    filter: 'brightness(0) invert(1)'
                                }
                            },

                        },
                        '&.MuiButton-outlinedError': {
                            '&:hover': {
                                backgroundColor: theme.palette.error.main,
                                color: '#fff',
                                '& .MuiButton-icon': {
                                    filter: 'brightness(0) invert(1)'
                                },
                                '& img': {
                                    filter: 'brightness(0) invert(1)'
                                }
                            }
                        },
                        '&.light': {
                            color: '#fff',
                            borderColor: '#fff',
                            '& .MuiButton-icon': {
                                filter: 'brightness(0) invert(1)',
                            },
                            '& img': {
                                filter: 'brightness(0) invert(1)',
                            },
                            '&:hover': {
                                // backgroundColor: '#0043a3',
                                color: '#002F73',
                                backgroundColor: '#fff',
                                '& .MuiButton-icon': {
                                    filter: 'none',
                                },
                                '& img': {
                                    filter: 'none',
                                },
                            }
                        },
                        '&.white': {
                            '&:not(:hover)': {
                                color: '#595959',
                                borderColor: '#BDBDBD',
                                backgroundColor: '#fff',
                            },
                            // '&:hover':{
                            //     backgroundColor: '#0043a3',
                            // }
                        },
                        '&.yellow': {
                            borderColor: '#E7D095',
                            backgroundColor: '#FFFAEF',
                            '&:hover': {
                                color: '#595959',
                                borderColor: '#E7D095',
                                backgroundColor: '#E7D095',
                                '& .MuiButton-icon': {
                                    filter: 'none !important',
                                    '& img': {
                                        filter: 'none !important'
                                    }
                                }
                            },
                        },
                        '&.blue':{
                            borderColor: '#BCD1F1',
                            backgroundColor: '#F9FBFF',
                            '&:hover': {
                                borderColor: '#2B62B1',
                                backgroundColor: '#2B62B1',
                            },
                        },
                    },
                    '&.MuiButton-text': {
                        fontWeight: 500,
                    },
                    '&.MuiButton-containedWarning': {
                        '&:not(:disabled)': {
                            backgroundColor: '#FF8635',
                            color: '#fff',
                            '& .MuiButton-icon': {
                                filter: 'brightness(0) invert(1)',
                                marginLeft: theme.spacing(0.5),
                                marginRight: theme.spacing(0.5),
                            },
                            '& img': {
                                filter: 'brightness(0) invert(1)',
                            },
                            '& .MuiButton-endIcon': {
                                marginLeft: theme.spacing(0.5),
                            },
                            '& .MuiButton-startIcon': {
                                marginRight: theme.spacing(0.5),
                            },
                            '&:hover': {
                                backgroundColor: '#ef721f',
                            },
                        },
                    },
                     "&.MuiButton-sizeSmall": {
                       minHeight: 38,
                       fontSize: 14,
                     },
                    '&.MuiButton-sizeLarge': {
                        minHeight: 60,
                        fontSize: 16,
                        lineHeight: '20px',
                        fontWeight: 600,
                    },
                    '&.xs': {
                        padding: theme.spacing(0.75, 1.5),
                        minHeight: '36px !important',
                    },
                    '&.xxs': {
                        padding: theme.spacing(0.5, 0.75),
                        fontSize: 13,
                        lineHeight: '18px',
                        fontWeight: 400,
                        minHeight: '26px !important',
                        '& .MuiButton-startIcon': {
                            marginRight: theme.spacing(0.5),
                        },
                        '& img': {
                            height: 16,
                            width: 'auto',
                        }
                    },
                    '&.w-auto': {
                        minWidth: '1px',
                    },

                    '&.live-chat-btn': {
                        minWidth: '60px !important',
                        minHeight: '60px !important',
                        padding: "8px",
                        '& img': {
                            width: 36,
                        }
                    },
                }),
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                    '&.MuiPaper-outlined': {
                        border: '1px solid #93B4E4',
                        '&.gray-border': {
                            borderColor: '#dadada'
                        }
                    }
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '& .MuiTableRow-head': {
                        background: '#F0F0F0',
                    },
                    '& .MuiTableCell-head': {
                        background: '#F0F0F0',
                        color: '#141414',
                        borderColor: '#E1E3EA',
                        fontSize: 14,
                        lineHeight: '16px',
                        fontWeight: 600,
                        padding: '18px 14px',
                    },
                    '& th.MuiTableCell-body': {
                        color: '#141414',
                        fontSize: 14.5,
                        lineHeight: '16px',
                        fontWeight: 600,
                    },
                    '& .MuiTableCell-body': {
                        color: '#3A3A3A',
                        borderColor: '#E1E3EA',
                        fontSize: 14,
                        lineHeight: '16px',
                        fontWeight: 400,
                        padding: '10px 14px',
                        '&.black': {
                            color: '#000',
                            fontWeight: 500,
                        },
                        '&.green': {
                            color: '#157400',
                            fontWeight: 500,
                        },
                        '&.red': {
                            color: '#eb1335',
                            fontWeight: 500,
                        },
                    },
                    '& .MuiTableCell-paddingCheckbox': {
                        padding: theme.spacing(0, 0, 0, 1),
                    },
                    '& .MuiCheckbox-root': {
                        '& .MuiSvgIcon-root': {
                            width: 20,
                            heuight: 20,
                        },
                        '&:not(.Mui-checked)': {
                            '& .MuiSvgIcon-root': {
                                fill: '#ccc',
                            },
                        },
                    },
                    '& .Mui-selected': {
                        '& .MuiTableCell-body': {
                            backgroundColor: '#d8ffd0',
                            borderColor: '#c7cbd5',
                        },
                    },
                    '& .Mui-selected-red': {
                        '& .MuiTableCell-body': {
                            backgroundColor: '#e3c4c4',
                            borderColor: '#c7cbd5',
                        },
                    },
                }),
            }
        },
        MuiChip: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.MuiChip-sizeSmall': {
                        minHeight: 26,
                    },
                    '&.green': {
                        background: '#32A232',
                        color: '#fff',
                    },
                    '&.lightGreen': {
                        background: '#B8FF97',
                        color: '#3A3A3A',
                    },
                    '&.lightRed': {
                        background: '#FFB8B8',
                        color: '#141414',
                    },
                    '&.red': {
                        background: '#f00',
                        color: '#fff',
                    },
                    '&.borderRed': {
                        background: '#ffe2e2',
                        color: '#fb0e0e',
                        border: '1px solid #fb0e0e',
                    },
                    '&.lightOrange': {
                        background: '#FFE3B6',
                        color: '#3A3A3A',
                    },
                    '&.yellow': {
                        background: '#FFDA1E',
                        color: '#3A3A3A',
                    },
                    '&.lightYellow': {
                        background: '#FFFC97',
                        color: '#3A3A3A',
                        '&.MuiChip-outlinedDefault': {
                            color: '#DF8919',
                            borderColor: '#F6C66E',
                            backgroundColor: '#FFFFE7',
                        }
                    },
                    '&.lightPurple': {
                        background: '#D8DEFE',
                        color: '#3A3A3A',
                    },
                    '&.square': {
                        borderRadius: 4,
                    },
                    '&.chip-500': {
                        fontWeight: 500,
                    },
                    '&.newChip': {
                        background: '#2ea813',
                        minHeight: 1,
                        height: 'auto',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        borderRadius: '0 0 6px 0',
                        '& .MuiChip-label': {
                            fontSize: 10,
                            lineHeight: '12px',
                            padding: '1px 10px 0px 10px',
                            minHeight: 15,
                        },
                    },
                    '&.border-chip': {
                        color: theme.palette.primary.main,
                        border: '1px solid #D9DFE9',
                        backgroundColor: '#FAFCFF',
                    },
                }),
            }
        },
        MuiDivider: {
            styleOverrides: {
                root: ({  }) => ({
                    width: '100%',
                    borderWidth: '1px',
                    borderColor: '#D1D5DB',
                    opacity: 1,
                }),
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: ({  }) => ({
                    '& ~ .MuiTypography-root': {
                        fontSize: 14,
                        lineHeight: '16px',
                        fontWeight: 500,
                    }
                }),
            }
        },
        MuiMenu: {
            styleOverrides: {
                root: ({  }) => ({
                    '& .MuiList-root': {
                        '& .MuiMenuItem-root': {
                            fontSize: 14,
                        }
                    }
                }),
            }
        },
        MuiAlert: {
            styleOverrides: {
                root: ({  }) => ({
                    fontSize: 13.5,
                    lineHeight: '18px',
                    '& .MuiAlertTitle-root': {
                        fontSize: 15,
                        lineHeight: '19px',
                        fontWeight: 600,
                    },
                    '&.MuiAlert-colorWarning': {
                        backgroundColor: '#fffbd1',
                    }
                }),
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: ({  }) => ({
                    borderRadius: 100,
                    backgroundColor: '#ededed',
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 100,
                        '&.MuiLinearProgress-barColorPrimary': {
                            backgroundColor: '#1D4C92',
                        }
                    }
                }),
            },
        },
    },
};

// Create and export theme
const theme = createTheme(themeOptions);

export const getTheme = (mode: "light" | "dark") => createTheme({
    palette: {
        mode,
        primary: { main: "#002F73" },
        secondary: { main: "#ff4081" },
        background: {
            default: mode === "dark" ? "#121212" : "#f4f6f8",
            paper: mode === "dark" ? "#1e1e1e" : "#ffffff",
        },
        text: {
            primary: mode === "dark" ? "#ffffff" : "#333333",
        },
    },
    typography: {
        fontFamily: "Poppins, sans-serif",
    },
});



export default theme;